
import React from 'react';
import { Link } from 'react-router-dom';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';
import AysenMap from '../components/AysenMap';
import { InstagramFeed } from '../components/InstagramFeed';

import { Expedition } from '../types';

interface HomeViewProps {
    t: any;
    lang: Language;
    expeditions?: Expedition[];
    homeSections?: any[];
    getExpField?: <T>(exp: any, field: string, lang: Language) => T;
}

const Home: React.FC<HomeViewProps> = ({ t, lang, expeditions, homeSections, getExpField }) => {
    const getSection = (id: string) => {
        return (homeSections || []).find(s => s.id === id);
    };

    const getSectionContent = (id: string, fallback: any) => {
        const sec = getSection(id);
        if (!sec) return { ...fallback, is_visible: true };
        const langContent = sec[`content_${lang}`];
        const esContent = sec.content_es || {};
        
        let content;
        if (lang === 'es') {
            content = esContent || fallback;
        } else {
            const cleanedLangContent: Record<string, any> = {};
            if (langContent && typeof langContent === 'object') {
                for (const [key, val] of Object.entries(langContent)) {
                    if (val && typeof val === 'string') {
                        const esVal = esContent[key];
                        // If DB value is untranslated Spanish copied into content_en, skip it so it uses the dictionary translation
                        if (esVal && typeof esVal === 'string' && val.trim() === esVal.trim()) {
                            continue;
                        }
                    }
                    cleanedLangContent[key] = val;
                }
            }
            content = { ...fallback, ...cleanedLangContent };
        }

        return {
            ...fallback,
            ...content,
            is_visible: sec.is_visible !== undefined ? sec.is_visible : true,
            bg_image_url: sec.bg_image_url || fallback.bg_image_url
        };
    };

    const hero = getSectionContent('hero', {
        tagline: t.hero_tagline,
        cta: t.hero_cta,
        bg_image_url: "https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg"
    });

    const about = getSectionContent('about', {
        category: t.nav_about,
        title: t.about_title,
        text: t.about_text
    });

    const philosophy = getSectionContent('philosophy', {
        phil_title: t.about_philosophy_title,
        phil_text: t.about_philosophy_text,
        loc_title: t.about_location_title,
        loc_text: t.about_location_text,
        safe_title: t.about_safety_title,
        safe_text: t.about_safety_text
    });

    const basecamp = getSectionContent('basecamp', {
        badge: t.nav_home === 'Home' ? 'Our Base Camp' : 'Nuestro Campamento Base',
        title: t.seo_village_title,
        text: t.seo_village_text,
        cta: t.featured_cta,
        bg_image_url: "https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/cerrocastillo.jpg"
    });

    const instagram = getSectionContent('instagram', {
        badge: 'INSTAGRAM',
        title: t.footer_instagram_title,
        cta: t.footer_instagram_follow
    });

    return (
        <div className="bg-[#fcfcfc] text-[#1a1a1a]">
            {/* 1. HERO SECTION (Sticky for parallex effect) */}
            {hero.is_visible && (
                <section className="relative h-screen w-full overflow-hidden bg-black">
                    {/* Background Image with immersive overlay */}
                    <div className="absolute inset-0">
                        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 z-10" />
                        <img
                            src={hero.bg_image_url}
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover scale-105 animate-slow-zoom"
                            alt="Expedición Trekking Laguna Cerro Castillo"
                        />
                    </div>
                    <h1 className="sr-only">Fisura Expeditions</h1>

                    {/* Bottom Content (Explora style) */}
                    <div className="absolute bottom-12 left-0 w-full z-20 px-8 flex flex-col md:flex-row items-center justify-between gap-6 animate-in slide-in-from-bottom duration-1000 delay-300">
                        <p className="text-sm md:text-base font-light tracking-[0.1em] text-[#f2f2f2]/90 max-w-xl text-center md:left drop-shadow-lg drop-shadow-black">
                            {hero.tagline}
                        </p>
                        <Link
                            to={getLocalizedPath('/expeditions', lang)}
                            className="group flex items-center space-x-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white hover:text-[#00d4ff] transition-all"
                        >
                            <span>{hero.cta}</span>
                            <span className="w-12 h-px bg-white group-hover:bg-[#00d4ff] transition-all group-hover:w-16"></span>
                        </Link>
                    </div>
                </section>
            )}

            {/* 2. ABOUT US SECTION (Explora style clean intro) */}
            <div className="relative z-20 bg-[#fcfcfc]">
                {about.is_visible && (
                    <section className="py-32 px-6 max-w-4xl mx-auto text-center space-y-12">
                        <div className="animate-in fade-in duration-1000">
                            <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-400 mb-6 block"> {about.category} </span>
                            <h2 className="text-3xl md:text-5xl font-title uppercase tracking-tighter leading-tight text-black mb-8">
                                {about.title}
                            </h2>
                            <div className="w-12 h-px bg-black/20 mx-auto mb-12" />
                            <p className="text-xl md:text-2xl font-light leading-relaxed text-gray-600 font-serif italic">
                                "{about.text}"
                            </p>
                        </div>
                    </section>
                )}

                {/* 3. EXPERIENCE SECTION (Stacked premium horizontal blocks) */}
                {philosophy.is_visible && (
                    <section className="py-24 px-6 max-w-7xl mx-auto space-y-8">
                        {/* Concept 1: Nuestra Filosofía */}
                        <div className="relative h-[400px] w-full overflow-hidden border border-gray-100/10 group rounded-sm shadow-xl">
                            {/* Background Image with slow zoom and grayscale hover */}
                            <div className="absolute inset-0 z-0">
                                <picture className="w-full h-full">
                                    <source srcSet="/images/Cerro Colorado/cerro-colorado-mountain-panorama.webp" type="image/webp" />
                                    <img
                                        src="/images/Cerro Colorado/cerro-colorado-mountain-panorama.jpg"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Cerro%20Colorado/cerro-colorado-mountain-panorama.jpg';
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-95 group-hover:scale-105 transition-all duration-1000 ease-out"
                                        alt="Nuestra Filosofía"
                                    />
                                </picture>
                                {/* Readability gradients */}
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent z-10" />
                            </div>

                            {/* Content */}
                            <div className="relative z-20 h-full flex items-center px-8 sm:px-16 md:px-24">
                                <div className="max-w-xl space-y-4">
                                    <span className="text-[#00d4ff] text-2xl font-title tracking-[0.2em] block">01</span>
                                    <h3 className="font-title text-3xl sm:text-4xl text-white uppercase tracking-widest">
                                        {philosophy.phil_title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-gray-300 font-light leading-relaxed uppercase tracking-widest font-sans">
                                        {philosophy.phil_text}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Concept 2: Ubicación Estratégica */}
                        <div className="relative h-[400px] w-full overflow-hidden border border-gray-100/10 group rounded-sm shadow-xl">
                            {/* Background Image with slow zoom and grayscale hover */}
                            <div className="absolute inset-0 z-0">
                                <picture className="w-full h-full">
                                    <source srcSet="/images/Travesía Las Horquetas/travesia-las-horquetas-drone-aysen.webp" type="image/webp" />
                                    <img
                                        src="/images/Travesía Las Horquetas/travesia-las-horquetas-drone-aysen.jpg"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Travesia%20Las%20Horquetas/travesia-las-horquetas-drone-aysen.jpg';
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-95 group-hover:scale-105 transition-all duration-1000 ease-out"
                                        alt="Ubicación Estratégica"
                                    />
                                </picture>
                                {/* Readability gradients - Alternated alignment (right to left) */}
                                <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/35 to-transparent z-10" />
                            </div>

                            {/* Content - Aligned to the right */}
                            <div className="relative z-20 h-full flex items-center justify-end px-8 sm:px-16 md:px-24">
                                <div className="max-w-xl space-y-4 text-right">
                                    <span className="text-[#00d4ff] text-2xl font-title tracking-[0.2em] block">02</span>
                                    <h3 className="font-title text-3xl sm:text-4xl text-white uppercase tracking-widest">
                                        {philosophy.loc_title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-gray-300 font-light leading-relaxed uppercase tracking-widest font-sans">
                                        {philosophy.loc_text}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Concept 3: Seguridad Activa */}
                        <div className="relative h-[400px] w-full overflow-hidden border border-gray-100/10 group rounded-sm shadow-xl">
                            {/* Background Image with slow zoom and grayscale hover */}
                            <div className="absolute inset-0 z-0">
                                <picture className="w-full h-full">
                                    <source srcSet="/images/Climbing Day/rock-climbing-walls-panorama.webp" type="image/webp" />
                                    <img
                                        src="/images/Climbing Day/rock-climbing-walls-panorama.jpg"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Climbing%20Day/rock-climbing-walls-panorama.jpg';
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:brightness-95 group-hover:scale-105 transition-all duration-1000 ease-out"
                                        alt="Seguridad Activa"
                                    />
                                </picture>
                                {/* Readability gradients */}
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent z-10" />
                            </div>

                            {/* Content */}
                            <div className="relative z-20 h-full flex items-center px-8 sm:px-16 md:px-24">
                                <div className="max-w-xl space-y-4">
                                    <span className="text-[#00d4ff] text-2xl font-title tracking-[0.2em] block">03</span>
                                    <h3 className="font-title text-3xl sm:text-4xl text-white uppercase tracking-widest">
                                        {philosophy.safe_title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-gray-300 font-light leading-relaxed uppercase tracking-widest font-sans">
                                        {philosophy.safe_text}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                <AysenMap expeditions={expeditions} lang={lang} t={t} getExpField={getExpField} />

                {/* 4. DESTINATION HIGHLIGHT (Immersive full-width Parallax) */}
                {basecamp.is_visible && (
                    <section className="relative w-full h-[75vh] min-h-[550px] flex items-center overflow-hidden bg-black">
                        {/* Background Parallax Image */}
                        <div
                            className="absolute inset-0 bg-cover bg-center bg-scroll md:bg-fixed bg-no-repeat z-0"
                            style={{ backgroundImage: `url('${basecamp.bg_image_url}')` }}
                        />
                        {/* Immersive Dark Gradient Overlays for maximum readability */}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent z-10" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/5 z-10" />

                        {/* Content Wrapper */}
                        <div className="relative z-20 max-w-7xl mx-auto w-full px-8 md:px-12 lg:px-16 flex items-center h-full">
                            <div className="max-w-2xl space-y-6 text-left">
                                <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#00d4ff] block animate-pulse">
                                    {basecamp.badge}
                                </span>
                                <h2 className="text-4xl sm:text-5xl md:text-6xl font-title uppercase tracking-tighter leading-[0.95] text-white">
                                    {basecamp.title}
                                </h2>
                                <p className="text-sm sm:text-base md:text-lg text-gray-200 font-light leading-relaxed max-w-xl font-sans">
                                    {basecamp.text}
                                </p>
                                <div className="pt-4">
                                    <Link
                                        to={getLocalizedPath('/expeditions', lang)}
                                        className="inline-block border-2 border-white text-white hover:border-[#00d4ff] hover:text-black hover:bg-[#00d4ff] px-8 py-3.5 font-title text-sm tracking-widest uppercase transition-all duration-300 shadow-lg hover:shadow-[#00d4ff]/20"
                                    >
                                        {basecamp.cta}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* 5. TAGLINE REINFORCEMENT & INSTAGRAM GRID */}
                {instagram.is_visible && (
                    <section className="py-24 bg-[#f9f9f9] border-y border-gray-100 overflow-hidden">
                        <div className="max-w-7xl mx-auto px-6 space-y-16">
                            <div className="max-w-4xl mx-auto text-center">
                                <p className="text-3xl md:text-5xl font-serif italic text-gray-800 leading-tight">
                                    " {hero.tagline} "
                                </p>
                            </div>

                            <div className="pt-8 border-t border-gray-200/60 space-y-8">
                                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                                    <div>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#00d4ff] block mb-2">
                                            {instagram.badge}
                                        </span>
                                        <h3 className="text-2xl md:text-3xl font-title uppercase tracking-tighter text-black">
                                            {instagram.title}
                                        </h3>
                                    </div>
                                    <a
                                        href="https://www.instagram.com/fisuraexpeditions/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[10px] font-bold tracking-[0.2em] text-gray-600 hover:text-black uppercase border-b border-black/20 pb-1 self-start sm:self-auto transition-colors"
                                    >
                                        {instagram.cta}
                                    </a>
                                </div>
                                <InstagramFeed />
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default Home;
